import React from "react";
import styled from "styled-components";
import { fetchWithAuth } from "../utils/authFetch";

/* LinkedIn company pages for a name, [{name, slug, id}], from our copy of LinkedIn's company directory (slug is null on
   pages Find Candidates added). A picked name is resolved to that exact page; a company not in the list still works by name. */
export async function searchLinkedInCompanies(q) {
  try {
    const res = await fetchWithAuth(`/api/linkedin-companies/search?q=${encodeURIComponent(q)}`);
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

const PlainInput = styled.input`
  width: 100%;
`;

/* One company with a LinkedIn dropdown; free text still counts. Earlier answers that still match show at once,
   and "Searching…" stands in while the lookup for the new text runs. `input` is the page's own styled input. */
export default function CompanyPicker({ value, onChange, placeholder, id, input: InputEl = PlainInput }) {
  const [hits, setHits] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [searching, setSearching] = React.useState(false);
  const wrapRef = React.useRef(null);
  const skipRef = React.useRef(false); // no lookup right after a pick
  const typedRef = React.useRef(false); // a prefilled value stays closed until the user types
  const seenRef = React.useRef(new Map()); // query -> rows, so a longer query reuses the shorter one's answer

  React.useEffect(() => {
    if (skipRef.current) {
      skipRef.current = false;
      return undefined;
    }
    const q = (value || "").trim();
    if (q.length < 2) {
      setHits([]);
      setOpen(false);
      setSearching(false);
      return undefined;
    }
    const lower = q.toLowerCase();
    const cached = seenRef.current.get(lower);
    if (cached) {
      setHits(cached);
      setOpen(typedRef.current && cached.length > 0);
      setSearching(false);
      return undefined;
    }
    let bestKey = "";
    for (const k of seenRef.current.keys()) {
      if (lower.startsWith(k) && k.length > bestKey.length) bestKey = k;
    }
    setHits(bestKey ? seenRef.current.get(bestKey).filter((r) => r.name.toLowerCase().includes(lower)) : []);
    setSearching(typedRef.current);
    setOpen(typedRef.current);
    let cancelled = false;
    const t = setTimeout(async () => {
      const rows = await searchLinkedInCompanies(q);
      if (cancelled) return;
      seenRef.current.set(lower, rows);
      setHits(rows);
      setSearching(false);
      setOpen(typedRef.current && rows.length > 0);
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [value]);

  React.useEffect(() => {
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <InputEl
        id={id}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        aria-expanded={open}
        onChange={(e) => {
          typedRef.current = true;
          onChange(e.target.value);
        }}
        onFocus={() => typedRef.current && (hits.length || searching) && setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape" && open) {
            e.stopPropagation(); // close the menu, not the dialog around it
            setOpen(false);
          }
        }}
      />
      {open && (hits.length > 0 || searching) && (
        <CompanyHits
          hits={hits}
          searching={searching}
          onPick={(row) => {
            skipRef.current = true;
            setOpen(false);
            setHits([]);
            setSearching(false);
            onChange(row.name);
          }}
        />
      )}
    </div>
  );
}

/* The dropdown under a company field or Find Candidates' Employers chips: one company name per row.
   mouseDown is cancelled so the field keeps focus (a blur would add the half-typed text as well). */
export function CompanyHits({ hits, onPick, searching = false }) {
  return (
    <CompanyMenu>
      {hits.map((row) => (
        <CompanyOption key={row.slug || row.id || row.name} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(row)}>
          <span>{row.name}</span>
        </CompanyOption>
      ))}
      {searching && (
        <CompanyOption type="button" disabled onMouseDown={(e) => e.preventDefault()} style={{ color: "var(--ink-muted)" }}>
          <span>Searching…</span>
        </CompanyOption>
      )}
    </CompanyMenu>
  );
}

const CompanyMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  max-height: 260px;
  overflow: auto;
  display: grid;
  gap: 0.1rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.line};
  background: ${({ theme }) => theme.surface};
  box-shadow: var(--shadow-lg);
  z-index: 30;
  padding: 0.4rem;
`;

const CompanyOption = styled.button`
  width: 100%;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.ink};
  text-align: left;
  padding: 7px 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  font: inherit;
  font-size: 13px;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--bg-2);
  }
  & > span:first-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

